import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { api, ApiError } from '@/api/client';
import { useAuthStore } from './auth';
import { useMascotStore } from './mascot';
import type { PetYardAction, PetYardSnapshot } from '@yana/shared/pet/yard';

export const usePetYardStore = defineStore('pet-yard', () => {
  const auth = useAuthStore(), mascot = useMascotStore();
  const snapshot = ref<PetYardSnapshot>({ members: [], scene: null, nextSceneAt: 0, serverTime: 0 });
  const visitId = ref<string | null>(null), roughPlay = ref(true), error = ref(''), busy = ref(false), loaded = ref(false);
  const mine = computed(() => snapshot.value.members.find(member => member.id === auth.user?.id && member.visitId === visitId.value));
  const visiting = computed(() => Boolean(visitId.value));
  let generation = 0, viewers = 0, started = false, polling = false, beating = false, lastBeat = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const key = () => `yana.mascot.${auth.user?.id}.yardVisit`;
  const optionsKey = () => `yana.mascot.${auth.user?.id}.yardRoughPlay`;
  const payload = () => ({ profile: { ...mascot.profile }, mood: mascot.care.mood, energy: mascot.care.energy, roughPlay: roughPlay.value });
  function remember(value: string | null): void {
    visitId.value = value; mascot.away = Boolean(value && mascot.enabled);
    try { if (value) localStorage.setItem(key(), value); else localStorage.removeItem(key()); } catch { /* Визит работает до закрытия вкладки. */ }
  }
  function accept(value: PetYardSnapshot, token: number, joining = false): boolean {
    if (token !== generation || !auth.user) return false;
    if (value.serverTime < snapshot.value.serverTime) return false;
    snapshot.value = value; loaded.value = true; error.value = '';
    const member = value.members.find(item => item.id === auth.user?.id);
    if (joining && member) remember(member.visitId);
    else if (visitId.value && member?.visitId !== visitId.value) remember(null);
    return true;
  }
  function failure(reason: unknown, token: number): void {
    if (token !== generation) return;
    error.value = reason instanceof Error ? reason.message : 'Не удалось связаться с двориком.';
  }
  async function refresh(): Promise<void> {
    if (!mascot.available || polling) return;
    const token = generation; polling = true;
    try { accept(await api.get<PetYardSnapshot>('/pet-yard'), token); }
    catch (reason) { failure(reason, token); }
    finally { if (token === generation) polling = false; }
  }
  async function heartbeat(): Promise<void> {
    if (!visitId.value || busy.value || beating || !mascot.enabled || !mascot.available) return;
    const token = generation, visit = visitId.value; beating = true;
    try {
      accept(await api.put<PetYardSnapshot>('/pet-yard/visit', { visitId: visit }), token);
      if (token === generation) lastBeat = Date.now();
    } catch (reason) {
      failure(reason, token);
      if (token === generation && reason instanceof ApiError && reason.status === 409) remember(null);
    } finally { if (token === generation) beating = false; }
  }
  async function join(): Promise<void> {
    if (busy.value || !mascot.enabled || !mascot.available) return;
    const token = generation; busy.value = true;
    try { if (accept(await api.post<PetYardSnapshot>('/pet-yard/visit', payload()), token, true)) lastBeat = Date.now(); }
    catch (reason) { failure(reason, token); }
    finally { if (token === generation) { busy.value = false; if (!mascot.enabled && visitId.value) void leave(); else schedule(); } }
  }
  async function leave(): Promise<void> {
    if (!visitId.value || busy.value) return;
    const token = generation, visit = visitId.value; busy.value = true;
    try { if (accept(await api.post<PetYardSnapshot>('/pet-yard/leave', { visitId: visit }), token)) remember(null); }
    catch (reason) { failure(reason, token); }
    finally { if (token === generation) { busy.value = false; schedule(); } }
  }
  async function interact(kind: PetYardAction, targetId: string): Promise<void> {
    if (!visitId.value || busy.value) return;
    const token = generation; busy.value = true;
    try { accept(await api.post<PetYardSnapshot>('/pet-yard/action', { visitId: visitId.value, targetId, kind }), token); }
    catch (reason) { await refresh(); failure(reason, token); }
    finally { if (token === generation) busy.value = false; }
  }
  async function setRoughPlay(value: boolean): Promise<void> {
    roughPlay.value = value;
    try { localStorage.setItem(optionsKey(), String(value)); } catch { /* Состояние текущей вкладки. */ }
    if (!visitId.value || !mascot.available) return;
    const token = generation;
    try { accept(await api.put<PetYardSnapshot>('/pet-yard/visit', { visitId: visitId.value, roughPlay: value }), token); }
    catch (reason) { failure(reason, token); }
  }
  async function tick(): Promise<void> {
    const token = generation;
    if (!mascot.available || !started) return;
    if (visitId.value && mascot.enabled && Date.now() - lastBeat >= 25_000) await heartbeat();
    else if (viewers > 0 && !document.hidden && !busy.value) await refresh();
    if (token === generation) schedule();
  }
  function schedule(): void {
    clearTimeout(timer);
    if (started && mascot.available && (viewers || visitId.value)) timer = setTimeout(() => void tick(), viewers && !document.hidden ? 2500 : 25_000);
  }
  function visibility(): void { if (!document.hidden) void tick(); else schedule(); }
  function storage(event: StorageEvent): void {
    if (event.key === optionsKey()) { roughPlay.value = event.newValue !== 'false'; return; }
    if (event.key !== key()) return;
    visitId.value = event.newValue; mascot.away = Boolean(event.newValue); void refresh(); schedule();
  }
  function start(): void { if (started) return; started = true; document.addEventListener('visibilitychange', visibility); window.addEventListener('storage', storage); schedule(); }
  function stop(): void { started = false; clearTimeout(timer); document.removeEventListener('visibilitychange', visibility); window.removeEventListener('storage', storage); }
  function open(): void { viewers++; void refresh(); schedule(); }
  function close(): void { viewers = Math.max(0, viewers - 1); schedule(); }
  watch(() => auth.user?.id, () => {
    generation++; clearTimeout(timer); polling = false; beating = false; busy.value = false; loaded.value = false; error.value = ''; lastBeat = 0;
    snapshot.value = { members: [], scene: null, nextSceneAt: 0, serverTime: 0 };
    try { visitId.value = localStorage.getItem(key()); roughPlay.value = localStorage.getItem(optionsKey()) !== 'false'; } catch { visitId.value = null; roughPlay.value = true; }
    mascot.away = Boolean(visitId.value && mascot.enabled);
    if (mascot.available && visitId.value) void refresh();
    schedule();
  }, { immediate: true });
  watch(() => mascot.enabled, value => { if (!value && visitId.value) { void leave(); mascot.away = false; } else schedule(); });
  return { snapshot, mine, visiting, roughPlay, error, busy, loaded, join, leave, interact, refresh, setRoughPlay, start, stop, open, close };
});
